import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PurchaseOrderService } from "src/services";
import { PurchaseOrder, UpdatePurchaseOrderComplianceParams } from "src/types";
import { queryKeys } from "src/constants";

export const useUpdatePurchaseOrderCompliance = (purchaseOrderService: PurchaseOrderService) => {
  const queryClient = useQueryClient();
  const [lastVariables, setLastVariables] = useState<UpdatePurchaseOrderComplianceParams | null>(null);

  const updatePurchaseOrderInCache = (updatedPurchaseOrder: PurchaseOrder) => {
    queryClient.setQueryData(queryKeys.purchaseOrder(updatedPurchaseOrder.id), () => updatedPurchaseOrder);
  };

  const {
    mutate: updateCompliance,
    isPending: isUpdatingCompliance,
    error: complianceUpdateError,
    reset: resetComplianceUpdate
  } = useMutation<PurchaseOrder, Error, UpdatePurchaseOrderComplianceParams>({
    mutationFn: ({
      purchaseOrderId,
      complianceStatus,
      complianceOfficer,
      regulatoryStandard,
      riskClassification,
      msdsRequired,
      complianceExpiryDate,
      complianceNoteBody,
      complianceFiles,
      initialComplianceFiles
    }) =>
      purchaseOrderService.updatePurchaseOrderCompliance(
        purchaseOrderId,
        complianceFiles ?? [],
        initialComplianceFiles ?? [],
        {
          complianceStatus,
          complianceOfficer,
          regulatoryStandard,
          riskClassification,
          msdsRequired,
          complianceExpiryDate,
          complianceNoteBody
        }
      ),
    onSuccess: updatePurchaseOrderInCache,
    onMutate: (variables) => {
      setLastVariables(variables);
    }
  });

  const retryComplianceUpdate = () => {
    if (lastVariables) {
      updateCompliance(lastVariables);
    }
  };

  return {
    updateCompliance,
    isUpdatingCompliance,
    complianceUpdateError,
    retryComplianceUpdate,
    resetComplianceUpdate
  };
};
